/**
 * Site refresh 2026: custom order page (task A33; redesign approved by Ray 1 Oct 2026).
 * Form spec: theme/docs/Custom-Order-Form-Spec.md.
 *
 * - Stage: a drifting row of customer photos (~30px/s, scrollLeft on each frame, the
 *   set repeated for a seamless loop). Stops on hover, focus and touch (resumes 3s after
 *   the finger lifts); Pause stops it; prefers-reduced-motion: no drift; off-screen: no frames.
 * - Progress bar: the step in view is current, the ones above it are done.
 * - Step 1: ?product=<handle> fills the piece in (Remove clears it); no product search.
 * - Step 2 picture: the product page's 3D view (refresh-size-3d.js, import()ed when step 2
 *   nears the screen; nothing without WebGL). Kind: the product page's own data-rp-scene-*
 *   (read from the start of its HTML, then the download stops) or the kind tile. Redraws
 *   150 ms after typing; once the field is still (600 ms, or change) the picture is framed
 *   on the typed size, bigger or smaller (co-3d-fit), in place (A60: same canvas, no loader); an
 *   empty or bad value keeps the last good one; in/cm only changes the pills.
 * - Files: checked here (type, size, count), big photos shrunk to 4000px JPEG, then
 *   POST <endpoint>/uploads for staged targets and uploaded straight to Shopify with progress.
 * - Turnstile loads on the first focus or input in the form, never on page load.
 * - Submit: POST <endpoint>/submit (JSON); every status answers JSON (A32: 400/403/422/429).
 *   Success swaps the form card for the sent card; a failure keeps everything typed.
 */
(() => {
	const root = document.querySelector('[data-rco]');
	if (!root) return;

	const form = root.querySelector('[data-rco-form]');
	// Liquid's t filter HTML-escapes (can&#39;t): decode once, the script only ever sets textContent
	const decoder = document.createElement('textarea');
	const decode = (v) => {
		if (typeof v === 'string') { decoder.innerHTML = v; return decoder.value; }
		if (v && typeof v === 'object') Object.keys(v).forEach((k) => { v[k] = decode(v[k]); });
		return v;
	};
	const i18n = decode(JSON.parse(root.querySelector('[data-rco-i18n]').textContent));
	const endpoint = root.dataset.endpoint.replace(/\/$/, '');
	const sitekey = root.dataset.sitekey;
	const shopRoot = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const MB = 1024 * 1024;
	const MAX_FILES = 10;
	const MAX_BYTES = 20 * MB;
	const MAX_TOTAL = 100 * MB;
	const TYPES = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', heic: 'image/heic', heif: 'image/heif', pdf: 'application/pdf' };
	const EMAIL_RE = /^[^\s@<>()",;:\\[\]]+@[^\s@<>()",;:\\[\]]+\.[a-z]{2,}$/i;
	const PHONE_RE = /^[0-9 +()\-]{1,40}$/;

	const $ = (sel, el = root) => el.querySelector(sel);
	const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));
	const fmt = (str, vars) => str.replace(/%(\w+)%/g, (m, k) => (k in vars ? vars[k] : m)); // placeholders from Liquid: %file%, %name%, %size% …
	const extOf = (name) => (name.toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';
	const field = (name) => form.elements[name];
	const checkedValue = (name) => { const el = form.querySelector(`input[name="${name}"]:checked`); return el ? el.value : ''; };
	const checkedLabel = (name) => {
		const el = form.querySelector(`input[name="${name}"]:checked`);
		if (!el) return '';
		const label = el.closest('label');
		const named = label.querySelector('.rco-swatch__name'); // skip hidden notes and icons
		return (named ? named.firstChild.textContent : label.textContent).replace(/\s+/g, ' ').trim();
	};
	const sized = (url, w) => {
		if (!url) return '';
		const u = url.startsWith('//') ? `https:${url}` : url;
		return `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
	};
	const sizeText = (bytes) => (bytes >= MB ? fmt(i18n.mb, { size: (bytes / MB).toFixed(1) }) : fmt(i18n.kb, { size: Math.max(1, Math.round(bytes / 1024)) }));

	const state = { startedAt: 0, product: null, files: [], token: '', widget: null, tsLoaded: false, sending: false, nextId: 1 };

	/* ---------------- Stage: drifting photo row ---------------- */
	(() => {
		const strip = $('[data-rco-strip]');
		const track = $('[data-rco-strip-track]');
		const btn = $('[data-rco-strip-pause]');
		if (!strip || !track) return;
		strip.setAttribute('tabindex', '0');
		strip.setAttribute('role', 'region');
		strip.setAttribute('aria-label', track.getAttribute('aria-label') || '');
		const SPEED = 30;
		let paused = false;
		let hover = false;
		let focus = false;
		let touch = false;
		let visible = false;
		let raf = 0;
		let last = 0;
		let pos = 0;
		let loop = 0;
		let resumeTimer = 0;

		if (!reduceMotion) {
			// Repeat the set once so the row wraps without a jump.
			const originals = $$('.rco-strip__item', track);
			originals.forEach((li) => {
				const copy = li.cloneNode(true);
				copy.setAttribute('aria-hidden', 'true');
				copy.removeAttribute('data-shopify-editor-block');
				$$('img', copy).forEach((img) => { img.alt = ''; img.loading = 'lazy'; });
				track.append(copy);
			});
			const measure = () => {
				const first = originals[0];
				const firstCopy = track.children[originals.length];
				loop = firstCopy && first ? firstCopy.offsetLeft - first.offsetLeft : 0;
			};
			measure();
			window.addEventListener('resize', measure);
			$$('img', track).forEach((img) => img.addEventListener('load', measure, { once: true }));
		}

		const moving = () => !reduceMotion && !paused && !hover && !focus && !touch && visible && loop > 0;
		const frame = (now) => {
			raf = 0;
			if (!moving()) return;
			const dt = last ? Math.min(now - last, 64) : 16;
			last = now;
			// Keep the position as a float: on a 1x screen scrollLeft rounds, so a step under a
			// pixel read back from it would never move.
			pos += (SPEED * dt) / 1000;
			if (pos >= loop) pos -= loop;
			strip.scrollLeft = pos;
			raf = requestAnimationFrame(frame);
		};
		const kick = () => {
			if (raf || !moving()) return;
			last = 0;
			pos = strip.scrollLeft % loop; // pick up where a swipe or keyboard scroll left it
			raf = requestAnimationFrame(frame);
		};

		strip.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hover = true; } });
		strip.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hover = false; kick(); } });
		strip.addEventListener('focusin', () => { focus = true; });
		strip.addEventListener('focusout', () => { focus = false; kick(); });
		strip.addEventListener('touchstart', () => { touch = true; clearTimeout(resumeTimer); }, { passive: true });
		strip.addEventListener('touchend', () => {
			clearTimeout(resumeTimer);
			resumeTimer = setTimeout(() => { touch = false; kick(); }, 3000);
		}, { passive: true });
		new IntersectionObserver((entries) => {
			visible = entries[0].isIntersecting;
			kick();
		}).observe(strip);

		if (btn) {
			if (reduceMotion) btn.hidden = true;
			btn.addEventListener('click', () => {
				paused = !paused;
				btn.setAttribute('aria-pressed', String(paused));
				btn.setAttribute('aria-label', paused ? btn.dataset.ariaPlay : btn.dataset.ariaPause);
				$('[data-rco-strip-pause-label]', btn).textContent = paused ? btn.dataset.labelPlay : btn.dataset.labelPause;
				kick();
			});
		}
	})();

	/* ---------------- Progress bar ---------------- */
	const steps = $$('[data-rco-step]');
	const progressItems = $$('[data-rco-progress]');
	let progressRaf = 0;
	function updateProgress() {
		progressRaf = 0;
		const line = window.innerHeight * 0.45;
		let current = 1;
		steps.forEach((step, i) => { if (step.getBoundingClientRect().top < line) current = i + 1; });
		progressItems.forEach((li, i) => {
			const n = i + 1;
			li.classList.toggle('is-done', n < current);
			if (n === current) li.setAttribute('aria-current', 'step');
			else li.removeAttribute('aria-current');
		});
	}
	window.addEventListener('scroll', () => { if (!progressRaf) progressRaf = requestAnimationFrame(updateProgress); }, { passive: true });
	window.addEventListener('resize', updateProgress);

	// Sticky offset: just under the theme's sticky header, whatever its height.
	function stickyTop() {
		const header = document.querySelector('.rn-header, header.header, #shopify-section-header, [class*="header-wrapper"]');
		let bottom = 0;
		if (header) {
			const box = header.getBoundingClientRect();
			const pos = getComputedStyle(header).position;
			if (pos === 'sticky' || pos === 'fixed') bottom = box.bottom;
		}
		if (bottom > 0 && bottom < 200) root.style.setProperty('--rco-sticky-top', `${Math.round(bottom + 8)}px`);
	}

	/* ---------------- Paths and size mode ---------------- */
	function showPath() {
		const path = checkedValue('path');
		$$('[data-rco-show]').forEach((el) => { el.hidden = el.dataset.rcoShow !== path; });
	}

	function showMode() {
		const mode = checkedValue('size_mode') || 'know';
		$$('[data-rco-mode-show]').forEach((el) => {
			const show = el.dataset.rcoModeShow === mode;
			el.hidden = !show || (el.hasAttribute('data-rco-shop-sizes') && !el.textContent.trim());
		});
	}

	function setMode(mode) {
		const input = form.querySelector(`input[name="size_mode"][value="${mode}"]`);
		if (input) input.checked = true;
		showMode();
	}

	form.addEventListener('change', (e) => {
		const name = e.target.name;
		if (name === 'path') {
			showPath();
			setMode(e.target.value === 'something_new' ? 'help' : 'know');
			if (e.target.value === 'shop_piece') buildPicker();
			updateStep2();
			pictureFromChoice();
		}
		if (name === 'piece') pickPiece(e.target.value);
		if (name === 'piece_kind') pictureFromChoice();
		if (name === 'size_mode') showMode();
		if (name === 'colour') pictureStain();
	});

	$$('[data-rco-unit]').forEach((btn) => {
		btn.addEventListener('click', () => setUnit(btn.dataset.rcoUnit));
	});

	function setUnit(unit) {
		const old = field('unit').value;
		if (old === unit) return;
		field('unit').value = unit;
		$$('[data-rco-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.rcoUnit === unit)));
		$$('[data-rco-unit-text]').forEach((el) => { el.textContent = unit === 'cm' ? i18n.unitCm : i18n.unitIn; });
		// The typed numbers keep their size: convert them.
		['width', 'depth', 'height', 'wall_width', 'wall_height'].forEach((name) => {
			const input = field(name);
			const n = parseFloat(String(input.value).replace(',', '.'));
			if (!Number.isFinite(n)) return;
			input.value = unit === 'cm' ? String(Math.round(n * 2.54)) : String(Math.round((n / 2.54) * 10) / 10);
		});
		pictureLabels();
		showShopSizes();
	}

	/* ---------------- Step 3: "Deliver to" (required since A43; no delivery note since A44) ---------------- */
	field('country').addEventListener('change', () => {
		if (field('country').value) showError('country', '');
	});

	const neededSelect = $('[data-rco-needed]');
	const neededDate = $('[data-rco-needed-date]');
	neededSelect.addEventListener('change', () => {
		const pick = neededSelect.value === 'date';
		neededDate.hidden = !pick;
		if (pick) {
			const d = new Date();
			neededDate.min = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
			neededDate.focus();
		}
	});

	// "Add a screenshot below" and "… in Photos just below": scroll to Photos, focus "Browse for files".
	const photosBox = $('.rco-photos');
	root.addEventListener('click', (e) => {
		const link = e.target.closest('a[href^="#"]');
		if (!link || !photosBox || link.getAttribute('href') !== `#${photosBox.id}`) return;
		e.preventDefault();
		photosBox.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
		// Show the ring even after a mouse click (focus-visible wouldn't), until focus moves on
		const input = $('[data-rco-file-input]');
		const dropBox = input.closest('.rco-drop');
		dropBox.classList.add('is-target');
		input.addEventListener('blur', () => dropBox.classList.remove('is-target'), { once: true });
		input.focus({ preventScroll: true });
	});

	/* ---------------- Step 1: "Which piece?" (recently viewed + ?product=) ---------------- */
	const pickList = $('[data-rco-pick-list]');
	const pickNone = $('[data-rco-pick-none]');
	const noPiece = $('[data-rco-nopiece]');
	const productCache = {};
	let pickerBuilt = false;

	const HANDLE_RE = /^[a-z0-9][a-z0-9\-_]*$/i;
	const urlHandle = () => {
		const h = new URLSearchParams(location.search).get('product');
		return h && HANDLE_RE.test(h) ? h.toLowerCase() : '';
	};
	// The product page keeps the last 13 handles, oldest first (localStorage theme_recently_viewed).
	const recentHandles = () => {
		try {
			const list = JSON.parse(localStorage.getItem('theme_recently_viewed') || '[]');
			return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && HANDLE_RE.test(h)).reverse() : [];
		} catch (e) {
			return [];
		}
	};

	function fetchProduct(handle) {
		if (!productCache[handle]) {
			productCache[handle] = fetch(`${shopRoot}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
				.then((res) => (res.ok ? res.json() : null))
				.then((p) => (p ? { handle: p.handle, title: p.title, image: p.featured_image || (p.images && p.images[0]) || '', options: p.options || [] } : null))
				.catch(() => null);
		}
		return productCache[handle];
	}

	// Tiles + "None of these" when there are pieces; the screenshot hint when there are none,
	// or when "None of these" is chosen.
	function showPickerState(hasPieces) {
		pickList.hidden = !hasPieces;
		$('[data-rco-pick-sub]').hidden = !hasPieces;
		$('[data-rco-pick-optional]').hidden = hasPieces;
		$('[data-rco-pick-not-here]').hidden = !hasPieces;
		const picked = form.querySelector('input[name="piece"]:checked');
		noPiece.hidden = hasPieces && !(picked && picked.value === '');
	}

	async function buildPicker() {
		if (pickerBuilt) return;
		pickerBuilt = true;
		const fromPage = urlHandle();
		const handles = [...new Set([fromPage, ...recentHandles()].filter(Boolean))].slice(0, 8);
		const found = (await Promise.all(handles.map(fetchProduct))).filter(Boolean).slice(0, 4);
		$$('.rco-pick__item', pickList).forEach((el) => el.remove());
		found.forEach((p) => {
			const label = document.createElement('label');
			label.className = 'rco-pick__item';
			const input = document.createElement('input');
			input.className = 'rco-sr-input';
			input.type = 'radio';
			input.name = 'piece';
			input.value = p.handle;
			const img = document.createElement('img');
			img.className = 'rco-pick__img';
			img.alt = '';
			img.width = 200;
			img.height = 132;
			if (p.image) img.src = sized(p.image, 400);
			const title = document.createElement('span');
			title.className = 'rco-pick__title';
			title.textContent = p.title;
			const tagRow = document.createElement('span');
			tagRow.className = 'rco-pick__tag-row';
			if (p.handle === fromPage) {
				const tag = document.createElement('span');
				tag.className = 'rco-pick__tag';
				tag.textContent = i18n.cameFrom;
				tagRow.append(tag);
			}
			const tick = document.createElement('span');
			tick.className = 'rco-pick__tick';
			tick.setAttribute('aria-hidden', 'true');
			tick.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
			label.append(input, img, title, tagRow, tick);
			pickList.insertBefore(label, pickNone);
		});
		const fromItem = fromPage && found.find((p) => p.handle === fromPage);
		if (fromItem) {
			pickList.querySelector(`input[value="${fromItem.handle}"]`).checked = true;
			pickPiece(fromItem.handle);
		}
		showPickerState(found.length > 0);
	}

	async function pickPiece(handle) {
		noPiece.hidden = handle !== '';
		if (!handle) {
			state.product = null;
		} else {
			const p = await fetchProduct(handle);
			if (checkedValue('piece') !== handle) return; // they picked another one meanwhile
			state.product = p ? { ...p, scene: null } : null;
		}
		markColours();
		showShopSizes();
		pic.kind = '';
		pic.typed = false;
		pictureFromChoice();
	}

	/* ---------------- Step 2: always the size part, then the colour part ---------------- */
	const sizePart = $('[data-rco-size-part]');
	const colourPart = $('[data-rco-colour-part]');
	function updateStep2() {
		sizePart.hidden = false;
		colourPart.hidden = false;
	}

	// `changes` from what they entered (no chips): Size if any size field has a value,
	// Colour or shade if a colour other than "Advise me" is picked.
	function enteredChanges() {
		const out = [];
		if (['width', 'depth', 'height', 'wall_width', 'wall_height'].some((n) => field(n).value.trim())) out.push('Size');
		const colour = checkedValue('colour');
		if (colour && colour !== 'Advise me') out.push('Colour or shade');
		return out;
	}

	// The product page's own size-view data: the same kind mapping, from the start of its HTML.
	async function productScene(handle) {
		try {
			const res = await fetch(`${shopRoot}products/${encodeURIComponent(handle)}`, { headers: { Accept: 'text/html' } });
			if (!res.ok || !res.body) return { kind: '' };
			const reader = res.body.getReader();
			const text = new TextDecoder();
			const re = /data-rp-scene-kind="([^"]*)"\s+data-rp-scene-size="([^"]*)"\s+data-rp-scene-max="([^"]*)"/;
			let buf = '';
			let read = 0;
			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;
				read += value.length;
				buf += text.decode(value, { stream: true });
				const m = buf.match(re);
				if (m) {
					reader.cancel().catch(() => {});
					const nums = (s) => s.split(',').map((n) => parseFloat(n) || 0);
					return { kind: m[1], size: nums(m[2]), max: nums(m[3]) };
				}
				if (read > 3 * MB) break;
				buf = buf.slice(-2000);
			}
			reader.cancel().catch(() => {});
		} catch (err) { /* no picture */ }
		return { kind: '' };
	}

	/* Shop sizes line: "In the shop this table comes 24, 32, 40 or 47″ wide and 12 to 24″ deep." */
	function shopSizesText(options) {
		const skip = /^(colou?r|finish|additional requirement)\b/i;
		const dims = options.filter((o) => !skip.test(String(o.name).trim()));
		const plain = (o) => String(o.name).replace(/\s*\((inches|inch|in|cm)\)\s*/i, '').trim();
		const hasWidth = dims.some((o) => /^width$/i.test(plain(o)));
		const hasDepth = dims.some((o) => /^depth$/i.test(plain(o)));
		const num = (v) => parseFloat(String(v).replace(/[^\d.]/g, ''));
		const uniq = (arr) => Array.from(new Set(arr)).sort((a, b) => a - b);
		const toUnit = (n) => (field('unit').value === 'cm' ? Math.round(n * 2.54) : n);
		const range = (arr, u) => (arr[0] === arr[arr.length - 1] ? `${toUnit(arr[0])}${u}` : fmt(i18n.to, { from: toUnit(arr[0]), to: `${toUnit(arr[arr.length - 1])}${u}` }));
		const list = (arr, u) => (arr.length < 2 ? `${toUnit(arr[0])}${u}` : `${arr.slice(0, -1).map(toUnit).join(', ')} ${i18n.or} ${toUnit(arr[arr.length - 1])}${u}`);
		const word = (name) => (/width/i.test(name) ? i18n.wide : /depth/i.test(name) ? i18n.deep : /height/i.test(name) ? i18n.high : '');
		const parts = [];
		dims.forEach((o) => {
			const name = plain(o);
			const values = (o.values || []).map(String);
			if (!values.length) return;
			const inch = /inch/i.test(o.name) || (hasWidth && hasDepth) || values.some((v) => /["″]/.test(v));
			const u = inch ? (field('unit').value === 'cm' ? ` ${i18n.unitCm}` : '″') : '';
			const names = name.split('&').map((s) => s.trim()).filter(Boolean);
			const pairs = values.map((v) => v.match(/(\d+(?:\.\d+)?)\s*["″]?\s*W\b.*?(\d+(?:\.\d+)?)\s*["″]?\s*H\b/i));
			if (names.length === 2 && pairs.every(Boolean)) {
				[1, 2].forEach((k, j) => {
					const w = word(names[j]);
					const text = range(uniq(pairs.map((m) => +m[k])), u);
					parts.push(w ? fmt(w, { sizes: text }) : `${names[j]} ${text}`);
				});
				return;
			}
			const nums = values.map(num);
			if (nums.some((n) => Number.isNaN(n))) return;
			const sorted = uniq(nums);
			const isWidth = /^width$/i.test(name);
			const text = isWidth && sorted.length <= 6 ? list(sorted, u) : range(sorted, u);
			const w = word(name);
			parts.push(w ? fmt(w, { sizes: text }) : `${name} ${text}`);
		});
		if (!parts.length) return '';
		const sizes = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} ${i18n.and} ${parts[parts.length - 1]}` : parts[0];
		const kind = state.product && state.product.scene ? state.product.scene.kind : '';
		const noun = ['table', 'console'].includes(kind) ? i18n.nouns.table
			: ['shelf', 'corner'].includes(kind) ? i18n.nouns.shelf
				: kind === 'nightstand' ? i18n.nouns.nightstand
					: kind === 'tree' ? i18n.nouns.bookshelf : i18n.nouns.piece;
		return fmt(i18n.shopLine, { noun, sizes });
	}

	function showShopSizes() {
		const line = $('[data-rco-shop-sizes]');
		line.textContent = state.product && checkedValue('path') === 'shop_piece' ? shopSizesText(state.product.options) : '';
		showMode();
	}

	function markColours() {
		const opt = state.product && state.product.options.find((o) => /^colou?r\b/i.test(String(o.name)));
		const own = opt ? opt.values.map((v) => String(v).toLowerCase()) : [];
		$$('[data-rco-colour]').forEach((label) => {
			const on = own.includes(label.dataset.rcoColour.toLowerCase());
			label.classList.toggle('is-in-product', on);
			const note = $('[data-rco-colour-note]', label);
			if (note) note.hidden = !on;
			// As on the product page: "Color as Shown in Picture" shows the piece's own photo
			if (/shown in picture|as shown/i.test(label.dataset.rcoColour)) {
				const fill = $('.rco-swatch__fill', label);
				if (fill.dataset.image === undefined) fill.dataset.image = fill.style.backgroundImage;
				fill.style.backgroundImage = state.product && state.product.image ? `url("${sized(state.product.image, 144)}")` : fill.dataset.image;
			}
		});
	}

	/* ---------------- Step 2: the 3D size picture ---------------- */
	const sizeBox = $('.rco-size');
	const sceneHost = $('[data-rco-scene-host]');
	const noPic = $('[data-rco-nopic]');
	// Example sizes (inches) the picture shows until the customer types: [width, depth, height].
	const EXAMPLE = { table: [60, 30, 16], dining: [72, 36, 30], console: [60, 14, 30], shelf: [48, 10, 2], corner: [24, 10, 2], art: [48, 3, 30], nightstand: [20, 14, 10], tree: [64, 8, 72] };
	// Which of width / depth / height (3: shelf thickness, as on the product page) get a pill.
	const PILLS = { table: [0, 1, 2], dining: [0, 1, 2], console: [0, 1, 2], shelf: [0, 1, 3], corner: [0, 1, 3], art: [0, 2], nightstand: [0, 1], tree: [0, 2] };
	const SHELVES = ['shelf', 'corner'];
	const LIMITS = [1, 240]; // inches the picture accepts
	// t: a shop shelf's own Thickness option (0 = none: "1–2″", drawn 2″, as on the product page)
	// unframed (A60): a typed size changed the model since the last framing check
	const pic = { kind: '', v: [0, 0, 0], max: [0, 0, 0], t: 0, typed: false, unframed: false, near: false, module: null, loading: null, failed: false };
	let picChain = Promise.resolve();
	const queue = (fn) => { picChain = picChain.then(fn).catch((e) => console.warn(e)); return picChain; };
	const hasWebGL = (() => {
		try {
			const c = document.createElement('canvas');
			const gl = c.getContext('webgl2') || c.getContext('webgl');
			const lose = gl && gl.getExtension('WEBGL_lose_context');
			if (lose) lose.loseContext();
			return !!gl;
		} catch (e) {
			return false;
		}
	})();

	function showNoPicture() {
		sceneHost.hidden = true;
		noPic.hidden = false;
	}

	function fieldsOnly() {
		pic.failed = true;
		sizeBox.classList.add('is-fields-only');
	}

	// Every new stage carries a loader; the CSS hides it once a frame is drawn (.is-3d).
	function newStage() {
		const stage = document.createElement('div');
		stage.className = 'rp-size__stage';
		const loader = document.createElement('span');
		loader.className = 'rco-picload';
		loader.setAttribute('aria-hidden', 'true');
		loader.textContent = i18n.pictureLoading;
		stage.append(loader);
		return stage;
	}

	// While a shop piece's page is read: an empty stage with the loader (no scene yet).
	function showLoading() {
		pic.kind = '';
		noPic.hidden = true;
		sceneHost.hidden = false;
		queue(async () => {
			if (pic.module) pic.module.reset(sceneHost);
			sceneHost.innerHTML = '';
			const panel = document.createElement('div');
			panel.className = 'rp-size';
			panel.append(newStage());
			sceneHost.append(panel);
		});
	}

	// The scene kind for the current choice: the product's (shop piece) or the tile's (something new).
	async function pictureFromChoice() {
		const path = checkedValue('path');
		let kind = '';
		let example = null;
		let max = null;
		let thick = 0;
		// "Something else" (A43): no picture column at all, the fields take the full width
		const tileNow = path === 'something_new' && form.querySelector('input[name="piece_kind"]:checked');
		sizeBox.classList.toggle('is-no-kind', !!tileNow && tileNow.dataset.rcoScene === 'none');
		if (path === 'shop_piece' && state.product) {
			if (!state.product.scene) {
				// Read the product page only when step 2 nears the screen, and only with WebGL.
				if (!pic.near || !hasWebGL) { showNoPicture(); return; }
				const handle = state.product.handle;
				state.product.scene = { kind: '' };
				showLoading();
				const scene = await productScene(handle);
				if (!state.product || state.product.handle !== handle) return;
				state.product.scene = scene;
				showShopSizes();
				if (checkedValue('path') !== 'shop_piece') return; // they switched to "Something new" meanwhile
			}
			const scene = state.product.scene;
			kind = scene.kind || '';
			if (kind && EXAMPLE[kind]) {
				const ex = EXAMPLE[kind];
				example = [0, 1, 2].map((i) => (scene.size && scene.size[i] > 0 ? scene.size[i] : ex[i]));
				max = [0, 1, 2].map((i) => (scene.max && scene.max[i] > 0 ? Math.max(scene.max[i], example[i]) : example[i]));
				if (SHELVES.includes(kind) && scene.size && scene.size[3] > 0) thick = scene.size[3];
			}
		} else if (path === 'something_new') {
			const tile = form.querySelector('input[name="piece_kind"]:checked');
			kind = tile && tile.dataset.rcoScene && tile.dataset.rcoScene !== 'none' ? tile.dataset.rcoScene : '';
		}
		if (!kind || !EXAMPLE[kind]) {
			pic.kind = '';
			queue(async () => { if (pic.module) pic.module.reset(sceneHost); sceneHost.innerHTML = ''; });
			showNoPicture();
			return;
		}
		if (kind === pic.kind && pic.typed) return; // keep what they typed for the same kind
		pic.kind = kind;
		pic.v = example || [...EXAMPLE[kind]];
		pic.max = max || [...pic.v];
		pic.t = thick;
		pic.typed = false;
		pic.unframed = false;
		applyTyped();
		drawPicture();
	}

	// Typed values (inches) override the example where they are good numbers.
	// Returns whether the model changed; the framing waits for frameTyped() (A60).
	function applyTyped() {
		const unit = field('unit').value;
		let changed = false;
		['width', 'depth', 'height'].forEach((name, i) => {
			const raw = parseFloat(String(field(name).value).replace(',', '.'));
			if (!Number.isFinite(raw)) return;
			const inches = unit === 'cm' ? raw / 2.54 : raw;
			if (inches < LIMITS[0] || inches > LIMITS[1]) return;
			if (pic.v[i] !== inches) changed = true;
			pic.v[i] = inches;
		});
		return changed;
	}

	// Once typing settles (A60): the picture is framed on the size they typed,
	// zooming out for a bigger one and in for a smaller one (co-3d-fit, Ray). Until
	// they type, the example keeps its frame. Half-typed numbers ("1", "12" on the
	// way to "120") never move the camera. Returns whether the frame changed.
	function frameTyped() {
		if (pic.unframed) pic.typed = true;
		pic.unframed = false;
		if (!pic.typed || pic.v.every((v, i) => Math.abs(v - pic.max[i]) < 0.01)) return false;
		pic.max = [...pic.v];
		return true;
	}

	const dimText = (v) => (field('unit').value === 'cm' ? `${Math.round(v * 2.54)} ${i18n.unitCm}` : `${Math.round(v * 10) / 10}″`);
	const pillText = (i) => {
		if (i === 3) {
			// Shelf thickness, as on the product page (task A46d): 1–2″ unless the shop shelf has its own.
			const range = field('unit').value === 'cm' ? `${Math.round(2.54)}–${Math.round(2 * 2.54)} ${i18n.unitCm}` : '1–2″';
			return `${i18n.thickness} ${pic.t > 0 ? dimText(pic.t) : range}`;
		}
		return `${i18n.dims[i]} ${dimText(pic.v[i])}`;
	};

	// Wall art side view (as on the product page): when the depth is known: typed,
	// a shop piece with a Depth option, or the example of "Something new".
	function sideOn() {
		if (pic.kind !== 'art') return false;
		const typed = parseFloat(String(field('depth').value).replace(',', '.'));
		if (Number.isFinite(typed) && typed > 0) return true;
		if (checkedValue('path') !== 'shop_piece') return true;
		return !!(state.product && state.product.options.some((o) => /depth/i.test(String(o.name))));
	}
	const sideText = () => `${i18n.dims[1]} ${dimText(pic.v[1])}`;

	function sideHTML(panel) {
		$$('.rp-size__side', panel).forEach((el) => el.remove());
		if (!sideOn()) return;
		const el = (tag, cls) => { const e = document.createElement(tag); e.className = cls; return e; };
		const side = el('div', 'rp-size__side');
		const draw = el('span', 'rp-size__side-pic');
		draw.setAttribute('aria-hidden', 'true');
		draw.style.setProperty('--rp-depth', String(pic.v[1]));
		draw.append(el('span', 'rp-size__side-wall'), el('span', 'rp-size__side-bar'), el('span', 'rp-size__side-dim'));
		const text = el('span', 'rp-size__side-text');
		const title = el('span', 'rp-size__side-title');
		title.textContent = i18n.sideView;
		const value = document.createElement('b');
		value.textContent = sideText();
		text.append(title, value, `: ${i18n.sideViewText}`);
		side.append(draw, text);
		panel.append(side);
	}

	function panelData() {
		const k = pic.kind;
		const [w, d, h] = pic.v;
		const [mw, md, mh] = pic.max;
		const shelf = SHELVES.includes(k);
		const size = shelf ? `${w},${d},0,${pic.t || EXAMPLE[k][2]}` : k === 'nightstand' ? `${w},${d},${EXAMPLE.nightstand[2]},0` : `${w},${d},${h},0`;
		const maxText = shelf ? `${mw},${md},0` : k === 'nightstand' ? `${mw},${md},${EXAMPLE.nightstand[2]}` : `${mw},${md},${mh}`;
		return { size, max: maxText };
	}

	function stainNow() {
		const input = form.querySelector('input[name="colour"]:checked');
		if (!input || !input.dataset.rcoStain || /shown in picture|as shown/i.test(input.value)) return '';
		return input.dataset.rcoStain;
	}

	function pillsHTML(stage) {
		$$('.rp-size__label', stage).forEach((el) => el.remove());
		(PILLS[pic.kind] || []).forEach((i) => {
			const span = document.createElement('span');
			span.className = 'rp-size__label';
			span.setAttribute('aria-hidden', 'true');
			span.textContent = pillText(i);
			stage.append(span);
		});
	}

	async function ensureModule() {
		if (pic.module) return pic.module;
		if (!pic.loading) pic.loading = import(root.dataset.size3d).then((m) => { pic.module = m; return m; });
		return pic.loading;
	}

	// A60: the panel (and its WebGL scene) is rebuilt only for a new kind or when
	// there is none yet. A new largest size only updates data-rp-scene-max, and
	// sync() reframes the same scene in place (no loader, the turn kept).
	function drawPicture() {
		if (!pic.kind) return;
		if (!hasWebGL || pic.failed) { fieldsOnly(); return; }
		noPic.hidden = true;
		sceneHost.hidden = false;
		if (!pic.near) return; // drawn once step 2 nears the screen
		queue(async () => {
			const mod = await ensureModule();
			const { size, max } = panelData();
			let panel = $('.rp-size', sceneHost);
			if (!panel || panel.dataset.rpSceneKind !== pic.kind) {
				mod.reset(sceneHost);
				sceneHost.innerHTML = '';
				panel = document.createElement('div');
				panel.className = 'rp-size';
				panel.setAttribute('data-rp-size', '');
				panel.setAttribute('data-rp-scene', '');
				panel.setAttribute('data-rp-scene-show-h', '');
				// Shelves: the Thickness line and pill, Depth at the left end (as on the product page)
				if (SHELVES.includes(pic.kind)) panel.setAttribute('data-rp-scene-label-t', '');
				panel.dataset.rpSceneKind = pic.kind;
				panel.append(newStage());
				sceneHost.append(panel);
			}
			panel.dataset.rpSceneMax = max;
			panel.dataset.rpSceneSize = size;
			panel.dataset.rpSceneStain = stainNow();
			pillsHTML($('.rp-size__stage', panel));
			sideHTML(panel);
			await mod.sync(sceneHost);
			const canvas = $('canvas', panel);
			if (!canvas) {
				// No WebGL after all (or the scene failed): fields only.
				fieldsOnly();
				return;
			}
			// A canvas without a frame yet (.is-3d) is not a failure: the picture was
			// hidden while the scene built (another choice clicked meanwhile). It draws
			// once the stage has a size again.
			const parts = (PILLS[pic.kind] || []).map(pillText);
			if (sideOn()) parts.push(sideText());
			canvas.setAttribute('aria-label', `${i18n.picture}: ${parts.join(', ')}`);
		});
	}

	function pictureLabels() {
		if (!pic.kind || pic.failed) return;
		drawPicture();
	}

	function pictureStain() {
		if (!pic.kind || pic.failed || !pic.near) return;
		drawPicture();
	}

	// The model follows each good value 150 ms after the keystroke; the framing
	// waits until the field has been still for 600 ms, or fires change / blur (A60).
	let typeTimer = 0;
	let settleTimer = 0;
	function followTyped() {
		if (!pic.kind || pic.failed) return;
		if (applyTyped()) {
			pic.unframed = true;
			drawPicture();
		}
	}
	function settleTyped() {
		clearTimeout(typeTimer);
		clearTimeout(settleTimer);
		if (!pic.kind || pic.failed) return;
		const changed = applyTyped();
		if (changed) pic.unframed = true;
		if (frameTyped() || changed) drawPicture();
	}
	$$('[data-rco-dim]').forEach((input) => {
		input.addEventListener('input', () => {
			clearTimeout(typeTimer);
			clearTimeout(settleTimer);
			typeTimer = setTimeout(followTyped, 150);
			settleTimer = setTimeout(settleTyped, 600);
		});
		input.addEventListener('change', settleTyped);
	});

	new IntersectionObserver((entries, observer) => {
		if (!entries[0].isIntersecting) return;
		observer.disconnect();
		pic.near = true;
		if (!hasWebGL) return;
		pic.kind = '';
		pictureFromChoice();
	}, { rootMargin: '600px 0px' }).observe($('[data-rco-size-step]'));

	/* ---------------- Files ---------------- */
	const fileInput = $('[data-rco-file-input]');
	const fileList = $('[data-rco-file-list]');
	const fileError = $('[data-rco-error-for="files"]');
	const drop = $('[data-rco-drop]');

	function showFileErrors(lines) {
		fileError.textContent = lines.join(' ');
		fileError.hidden = !lines.length;
	}

	async function shrink(file) {
		const ext = extOf(file.name);
		if (!['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext) || file.size <= 4 * MB || !window.createImageBitmap) return file;
		let bmp;
		try {
			bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
		} catch (err) {
			return file; // can't decode here (e.g. HEIC on Chrome/Windows): upload as it is
		}
		if (bmp.width * bmp.height <= 20e6 && file.size <= MAX_BYTES) { if (bmp.close) bmp.close(); return file; }
		const scale = Math.min(1, 4000 / Math.max(bmp.width, bmp.height));
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(bmp.width * scale);
		canvas.height = Math.round(bmp.height * scale);
		canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
		if (bmp.close) bmp.close();
		const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
		if (!blob) return file;
		return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
	}

	async function addFiles(list) {
		const errors = [];
		const added = [];
		for (const original of Array.from(list)) {
			if (state.files.length >= MAX_FILES) { if (!errors.includes(i18n.e5)) errors.push(i18n.e5); break; }
			const ext = extOf(original.name);
			if (!TYPES[ext]) { errors.push(fmt(i18n.e3, { file: original.name })); continue; }
			const file = await shrink(original);
			if (file.size > MAX_BYTES) { errors.push(fmt(i18n.e4, { file: original.name })); continue; }
			const total = state.files.reduce((n, f) => n + f.size, 0) + file.size;
			if (total > MAX_TOTAL) { errors.push(fmt(i18n.e4, { file: original.name })); continue; }
			const type = file.type || TYPES[extOf(file.name)] || '';
			const entry = {
				id: state.nextId++,
				file,
				name: file.name,
				size: file.size,
				type,
				status: 'queued',
				progress: 0,
				resourceUrl: '',
				thumb: /^image\//.test(type) ? URL.createObjectURL(file) : '',
			};
			entry.done = new Promise((resolve) => { entry.resolve = resolve; });
			state.files.push(entry);
			added.push(entry);
		}
		showFileErrors(errors);
		renderFiles();
		if (added.length) requestTargets(added);
	}

	async function requestTargets(entries) {
		entries.forEach((e) => { e.status = 'uploading'; e.progress = 0; e.code = ''; });
		renderFiles();
		let data = null;
		try {
			const res = await fetch(`${endpoint}/uploads`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ files: entries.map((e) => ({ name: e.name, size: e.size, type: e.type })) }),
			});
			data = await res.json().catch(() => null);
		} catch (err) {
			data = null;
		}
		if (data && Array.isArray(data.targets) && data.targets.length === entries.length) {
			entries.forEach((e, i) => upload(e, data.targets[i]));
			return;
		}
		const code = (data && data.error) || 'E9';
		const perFile = (data && data.files) || [];
		const lines = [];
		entries.forEach((e) => {
			const hit = perFile.find((f) => f.file === e.name);
			finish(e, 'error', hit ? hit.code : code === 'E8' ? 'E8' : 'E6');
		});
		if (code === 'E8') lines.push(i18n.e8);
		perFile.forEach((f) => {
			if (f.code === 'E3') lines.push(fmt(i18n.e3, { file: f.file || '' }));
			if (f.code === 'E4') lines.push(fmt(i18n.e4, { file: f.file || '' }));
			if (f.code === 'E5') lines.push(i18n.e5);
		});
		if (lines.length) showFileErrors(lines);
	}

	function upload(entry, target) {
		if (!state.files.includes(entry)) return;
		const fd = new FormData();
		(target.parameters || []).forEach((p) => fd.append(p.name, p.value));
		fd.append('file', entry.file, entry.name);
		const xhr = new XMLHttpRequest();
		entry.xhr = xhr;
		xhr.open('POST', target.url);
		xhr.upload.addEventListener('progress', (e) => {
			if (!e.lengthComputable) return;
			entry.progress = Math.round((e.loaded / e.total) * 100);
			const bar = fileList.querySelector(`[data-file-id="${entry.id}"] .rco-file__bar span`);
			if (bar) bar.style.width = `${entry.progress}%`;
		});
		xhr.addEventListener('load', () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				entry.resourceUrl = target.resourceUrl;
				finish(entry, 'done');
			} else finish(entry, 'error', 'E6');
		});
		xhr.addEventListener('error', () => finish(entry, 'error', 'E6'));
		xhr.addEventListener('abort', () => finish(entry, 'removed'));
		xhr.send(fd);
	}

	function finish(entry, status, code = '') {
		entry.status = status;
		entry.code = code;
		entry.xhr = null;
		entry.resolve();
		renderFiles();
	}

	function removeFile(id) {
		const i = state.files.findIndex((f) => f.id === id);
		if (i < 0) return;
		const [entry] = state.files.splice(i, 1);
		if (entry.xhr) entry.xhr.abort();
		entry.resolve();
		if (entry.thumb) URL.revokeObjectURL(entry.thumb);
		renderFiles();
		showFileErrors([]);
		const next = fileList.querySelector('.rco-file__remove') || fileInput;
		next.focus();
	}

	function renderFiles() {
		fileList.innerHTML = '';
		state.files.forEach((entry) => {
			const li = document.createElement('li');
			li.className = `rco-file${entry.status === 'error' ? ' is-error' : ''}`;
			li.dataset.fileId = entry.id;
			const thumb = document.createElement('span');
			thumb.className = 'rco-file__thumb';
			if (entry.thumb) {
				const img = document.createElement('img');
				img.src = entry.thumb;
				img.alt = '';
				img.addEventListener('error', () => { img.remove(); thumb.textContent = extOf(entry.name); });
				thumb.append(img);
			} else thumb.textContent = extOf(entry.name);
			const body = document.createElement('span');
			body.className = 'rco-file__body';
			const name = document.createElement('b');
			name.className = 'rco-file__name';
			name.textContent = entry.name;
			const meta = document.createElement('span');
			meta.className = 'rco-file__meta';
			if (entry.status === 'error') {
				meta.textContent = entry.code === 'E8' ? i18n.e8 : fmt(i18n.e6, { file: entry.name });
				meta.id = `${root.id}-file-${entry.id}-error`;
			} else meta.textContent = `${sizeText(entry.size)}${entry.status === 'uploading' ? ` · ${i18n.uploading}` : ''}`;
			body.append(name, meta);
			if (entry.status === 'error' && entry.code !== 'E8') {
				const retry = document.createElement('button');
				retry.type = 'button';
				retry.className = 'rco-file__retry';
				retry.textContent = i18n.tryAgain;
				retry.setAttribute('aria-describedby', meta.id);
				retry.addEventListener('click', () => requestTargets([entry]));
				body.append(retry);
			}
			const remove = document.createElement('button');
			remove.type = 'button';
			remove.className = 'rco-file__remove';
			remove.setAttribute('aria-label', fmt(i18n.remove, { name: entry.name }));
			remove.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
			remove.addEventListener('click', () => removeFile(entry.id));
			li.append(thumb, body, remove);
			if (entry.status === 'uploading') {
				const bar = document.createElement('span');
				bar.className = 'rco-file__bar';
				bar.setAttribute('aria-hidden', 'true');
				const fill = document.createElement('span');
				fill.style.width = `${entry.progress}%`;
				bar.append(fill);
				li.append(bar);
			}
			fileList.append(li);
		});
		fileList.hidden = !state.files.length;
	}

	fileInput.addEventListener('change', () => {
		if (fileInput.files.length) addFiles(fileInput.files);
		fileInput.value = '';
	});
	['dragenter', 'dragover'].forEach((type) => drop.addEventListener(type, (e) => {
		e.preventDefault();
		drop.classList.add('is-over');
	}));
	['dragleave', 'drop'].forEach((type) => drop.addEventListener(type, (e) => {
		e.preventDefault();
		drop.classList.remove('is-over');
	}));
	drop.addEventListener('drop', (e) => {
		startInteraction();
		if (e.dataTransfer && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
	});

	/* ---------------- Summary (sent state) ---------------- */
	function summaryRows() {
		const R = i18n.rows;
		const rows = [];
		const path = checkedValue('path');
		if (path === 'something_new') {
			rows.push([R.start, i18n.paths.something_new]);
			if (checkedValue('piece_kind')) rows.push([R.piece, checkedValue('piece_kind')]);
			if (checkedValue('room')) rows.push([R.room, checkedLabel('room')]);
		}

		const unit = field('unit').value;
		const val = (n) => field(n).value.trim();
		if (sizePart.hidden) { /* no size asked */ } else if ((checkedValue('size_mode') || 'know') === 'know') {
			const dims = ['width', 'depth', 'height'].map(val).filter(Boolean);
			if (dims.length) rows.push([R.size, `${dims.join(' × ')} ${unit}`]);
		} else {
			const dims = ['wall_width', 'wall_height'].map(val).filter(Boolean);
			if (dims.length) rows.push([R.space, fmt(i18n.wall, { size: `${dims.join(' × ')} ${unit}` })]);
		}
		if (!colourPart.hidden && checkedValue('colour')) rows.push([R.colour, checkedLabel('colour')]);
		const country = field('country');
		if (country && country.selectedIndex >= 0) rows.push([R.deliver, country.options[country.selectedIndex].text]);
		return rows;
	}

	function renderRows(dl, rows) {
		dl.innerHTML = '';
		rows.forEach(([k, v]) => {
			const div = document.createElement('div');
			div.className = 'rco-summary__row';
			const dt = document.createElement('dt');
			dt.textContent = k;
			const dd = document.createElement('dd');
			dd.textContent = v;
			div.append(dt, dd);
			dl.append(div);
		});
	}

	function renderProduct(box) {
		box.innerHTML = '';
		const show = state.product && checkedValue('path') === 'shop_piece';
		box.hidden = !show;
		if (!show) return;
		const media = document.createElement('span');
		if (state.product.image) {
			const img = document.createElement('img');
			img.src = sized(state.product.image, 120);
			img.alt = '';
			img.width = 52;
			img.height = 52;
			media.append(img);
		}
		const name = document.createElement('b');
		name.textContent = state.product.title;
		box.append(media, name);
	}

	/* ---------------- Turnstile (loaded on first interaction) ---------------- */
	function renderTurnstile() {
		const slot = $('[data-rco-turnstile]');
		if (!window.turnstile || state.widget !== null || !sitekey) return;
		state.widget = window.turnstile.render(slot, {
			sitekey,
			action: 'custom-order',
			'response-field': false,
			callback: (token) => { state.token = token; showError('turnstile', ''); },
			'expired-callback': () => { state.token = ''; },
			'error-callback': () => { state.token = ''; },
		});
	}

	function loadTurnstile() {
		if (state.tsLoaded) return;
		state.tsLoaded = true;
		window.rcoTurnstileReady = renderTurnstile;
		const s = document.createElement('script');
		s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=rcoTurnstileReady';
		s.async = true;
		s.defer = true;
		document.head.append(s);
	}

	function startInteraction() {
		if (!state.startedAt) state.startedAt = Date.now();
		loadTurnstile();
		// The kind tiles sit in a hidden panel: fetch their photos now so they don't pop in grey
		$$('.rco-kind img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
	}
	form.addEventListener('focusin', startInteraction);
	form.addEventListener('input', startInteraction);

	/* ---------------- Errors ---------------- */
	const errorFields = { first_name: 'first_name', email: 'email', phone: 'phone', country: 'country', message: 'message' };
	const alertBox = $('[data-rco-alert]');

	function showError(name, text) {
		const p = $(`[data-rco-error-for="${name}"]`);
		if (p) {
			p.textContent = text;
			p.hidden = !text;
		}
		const input = errorFields[name] ? field(name) : null;
		if (input) {
			if (text) input.setAttribute('aria-invalid', 'true');
			else input.removeAttribute('aria-invalid');
		}
	}

	function showAlert(text) {
		alertBox.textContent = text;
		alertBox.hidden = !text;
	}

	function clearErrors() {
		['first_name', 'email', 'phone', 'country', 'message', 'turnstile'].forEach((n) => showError(n, ''));
		showAlert('');
	}

	function validate() {
		const errs = [];
		const v = (n) => field(n).value.trim();
		if (!v('first_name')) errs.push(['first_name', i18n.e1]);
		if (!v('email')) errs.push(['email', i18n.e1]);
		else if (v('email').length > 254 || !EMAIL_RE.test(v('email'))) errs.push(['email', i18n.e2]);
		if (!v('phone') || !PHONE_RE.test(v('phone')) || !/\d/.test(v('phone'))) errs.push(['phone', i18n.e1]);
		if (!v('country')) errs.push(['country', i18n.e1]);
		return errs;
	}

	function focusFirstError(errs) {
		const first = errs[0];
		if (!first) return;
		if (first[0] === 'turnstile') {
			const slot = $('[data-rco-turnstile]');
			slot.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
			slot.setAttribute('tabindex', '-1');
			slot.focus({ preventScroll: true });
		} else if (first[0] === 'files') {
			const btn = fileList.querySelector('.rco-file__retry') || fileInput;
			btn.focus();
		} else field(first[0]).focus();
	}

	/* ---------------- Submit ---------------- */
	const submitBtn = $('[data-rco-submit]');
	const submitLabel = $('[data-rco-submit-label]');
	function setSending(on) {
		state.sending = on;
		submitBtn.disabled = on;
		submitLabel.textContent = on ? i18n.sending : submitBtn.dataset.label;
	}

	function collect() {
		const v = (n) => field(n).value.trim();
		const mode = checkedValue('size_mode') || 'know';
		const path = checkedValue('path') || 'not_chosen';
		const body = {
			path,
			product_handle: path === 'shop_piece' && state.product && checkedValue('piece') ? state.product.handle : '',
			changes: path === 'shop_piece' ? enteredChanges() : [],
			piece_kind: path === 'something_new' ? checkedValue('piece_kind') : '',
			room: path === 'something_new' ? checkedValue('room') : '',
			size_mode: mode,
			unit: field('unit').value,
			colour: colourPart.hidden ? '' : checkedValue('colour'),
			files: state.files.filter((f) => f.status === 'done').map((f) => f.resourceUrl),
			first_name: v('first_name'),
			last_name: v('last_name'),
			email: v('email'),
			phone: v('phone'),
			country: field('country').value,
			contact_pref: checkedValue('contact_pref') || 'Email',
			message: field('message').value.trim(),
			budget: checkedValue('budget'),
			needed_by: neededSelect.value === 'date' ? neededDate.value : 'No rush',
			trade: field('trade').checked,
			page_url: `${location.origin}${location.pathname}`,
			started_at: state.startedAt,
			website: field('website').value,
			'cf-turnstile-response': state.token || (window.turnstile && state.widget !== null ? window.turnstile.getResponse(state.widget) || '' : ''),
		};
		if (sizePart.hidden) return body; // Size not asked for: send no size
		if (mode === 'know') ['width', 'depth', 'height'].forEach((n) => { if (v(n)) body[n] = v(n); });
		else {
			['wall_width', 'wall_height'].forEach((n) => { if (v(n)) body[n] = v(n); });
			body.furniture_below = v('furniture_below');
		}
		return body;
	}

	form.addEventListener('submit', async (e) => {
		e.preventDefault();
		if (state.sending) return;
		startInteraction();
		clearErrors();
		const errs = validate();
		errs.forEach(([n, text]) => showError(n, text));
		const token = state.token || (window.turnstile && state.widget !== null ? window.turnstile.getResponse(state.widget) : '');
		if (!token) { showError('turnstile', i18n.e7); errs.push(['turnstile', i18n.e7]); }
		if (state.files.some((f) => f.status === 'error')) errs.push(['files', '']);
		if (errs.length) {
			showAlert(i18n.check);
			focusFirstError(errs);
			return;
		}

		setSending(true);
		await Promise.all(state.files.filter((f) => f.status === 'uploading' || f.status === 'queued').map((f) => f.done));
		if (state.files.some((f) => f.status === 'error')) {
			setSending(false);
			showAlert(i18n.check);
			focusFirstError([['files', '']]);
			return;
		}

		const body = collect();
		let data = null;
		const ctrl = new AbortController();
		const timer = setTimeout(() => ctrl.abort(), 30000);
		try {
			const res = await fetch(`${endpoint}/submit`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify(body),
				signal: ctrl.signal,
			});
			data = await res.json().catch(() => null);
		} catch (err) {
			data = null;
		}
		clearTimeout(timer);
		setSending(false);

		if (data && data.ok) {
			showSent(data.ref, body);
			return;
		}
		const code = (data && data.error) || 'E9';
		const fields = (data && data.fields) || {};
		const fieldErrs = Object.keys(fields).filter((k) => errorFields[k]).map((k) => [k, fields[k] === 'E2' ? i18n.e2 : i18n.e1]);
		fieldErrs.forEach(([n, text]) => showError(n, text));
		if (code === 'E7') {
			showError('turnstile', i18n.e7);
			state.token = '';
			if (window.turnstile && state.widget !== null) window.turnstile.reset(state.widget);
			showAlert(i18n.e7);
			focusFirstError([['turnstile']]);
		} else if (fieldErrs.length) {
			showAlert(i18n.check);
			focusFirstError(fieldErrs);
		} else if (code === 'E5') {
			showAlert(i18n.e5);
		} else {
			showAlert(code === 'E8' ? i18n.e8 : i18n.e9);
			// A used token can't be checked twice: get a fresh one for the next try
			state.token = '';
			if (window.turnstile && state.widget !== null) window.turnstile.reset(state.widget);
			submitBtn.focus();
		}
	});

	/* ---------------- Sent state ---------------- */
	const card = $('[data-rco-card]');
	const sent = $('[data-rco-sent]');

	function showSent(ref, body) {
		const R = i18n.rows;
		const rows = [[R.ref, ref], ...summaryRows()];
		rows.push([R.reach, checkedLabel('contact_pref')]);
		rows.push([R.email, body.email]);
		renderRows($('[data-rco-sent-rows]'), rows);
		renderProduct($('[data-rco-sent-product]'));
		const thumbs = $('[data-rco-sent-thumbs]');
		thumbs.innerHTML = '';
		const done = state.files.filter((f) => f.status === 'done');
		done.forEach((f) => {
			const span = document.createElement('span');
			if (f.thumb) {
				const img = document.createElement('img');
				img.src = f.thumb;
				img.alt = f.name;
				img.addEventListener('error', () => { img.remove(); span.textContent = extOf(f.name); });
				span.append(img);
			} else span.textContent = extOf(f.name);
			thumbs.append(span);
		});
		$('[data-rco-sent-files]').hidden = !done.length;
		card.hidden = true;
		sent.hidden = false;
		const body0 = $('.rco-sent__body', sent);
		body0.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
		$('[data-rco-sent-focus]').focus({ preventScroll: true });
	}

	$('[data-rco-again]').addEventListener('click', () => {
		state.files.forEach((f) => { if (f.xhr) f.xhr.abort(); if (f.thumb) URL.revokeObjectURL(f.thumb); });
		state.files = [];
		state.product = null;
		state.startedAt = 0;
		state.token = '';
		if (window.turnstile && state.widget !== null) window.turnstile.reset(state.widget);
		form.reset();
		field('unit').value = 'in';
		$$('[data-rco-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.rcoUnit === 'in')));
		$$('[data-rco-unit-text]').forEach((el) => { el.textContent = i18n.unitIn; });
		neededDate.hidden = true;
		$$('[data-rco-more]').forEach((d) => { d.open = false; });
		showPickerState(!!$('.rco-pick__item', pickList));
		renderFiles();
		showFileErrors([]);
		clearErrors();
		markColours();
		showPath();
		showMode();
		showShopSizes();
		updateStep2();
		pic.kind = '';
		pic.typed = false;
		pictureFromChoice();
		sent.hidden = true;
		card.hidden = false;
		card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
		const first = form.querySelector('input[name="path"]');
		if (first) first.focus({ preventScroll: true });
		fromUrl();
	});

	/* ---------------- Start ---------------- */
	function fromUrl() {
		if (!urlHandle()) return;
		const shop = form.querySelector('input[name="path"][value="shop_piece"]');
		shop.checked = true;
		showPath();
		setMode('know');
		updateStep2();
		pickerBuilt = false;
		buildPicker();
	}

	if (!hasWebGL) fieldsOnly();
	showPath();
	showMode();
	updateStep2();
	stickyTop();
	updateProgress();
	fromUrl();
})();
